import React, { useState, useEffect } from 'react';
import { useGoogleDriveClient } from './googleDrive.client';
import { Button, Icon, Segment, List } from 'semantic-ui-react';
import { InComponentErrorRenderer } from '../../components/InComponentErrorRenderer';
export const GoogleDriveOAuthSetup = ({ onSuccess, onError }) => {
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const driveClient = useGoogleDriveClient();
    // Load existing integrations on component mount
    useEffect(() => {
        loadIntegrations();
    }, []);
    const loadIntegrations = async () => {
        try {
            setLoading(true);
            const data = await driveClient.listIntegrations();
            setIntegrations(data);
        }
        catch (err) {
            console.error('Error loading Google Drive integrations:', err);
            setError('Failed to load Google Drive integrations');
        }
        finally {
            setLoading(false);
        }
    };
    const handleConnect = async () => {
        try {
            setLoading(true);
            setError(null);
            const { authUrl } = await driveClient.getAuthUrl();
            // Open Google OAuth in a popup window
            const popup = window.open(authUrl, 'google-drive-auth', 'width=500,height=600,scrollbars=yes,resizable=yes');
            // Poll for popup closure (indicates OAuth completion)
            const checkClosed = setInterval(() => {
                if (popup?.closed) {
                    clearInterval(checkClosed);
                    // Reload integrations after OAuth completion
                    setTimeout(() => {
                        loadIntegrations();
                    }, 1000);
                }
            }, 1000);
        }
        catch (err) {
            console.error('Error starting Google Drive OAuth:', err);
            setError('Failed to start Google Drive connection');
        }
        finally {
            setLoading(false);
        }
    };
    const handleDisconnect = async (integrationId) => {
        try {
            setLoading(true);
            await driveClient.deleteIntegration(integrationId);
            await loadIntegrations(); // Reload the list
        }
        catch (err) {
            console.error('Error disconnecting Google Drive:', err);
            setError('Failed to disconnect Google Drive integration');
        }
        finally {
            setLoading(false);
        }
    };
    const handleActivate = async (integrationId) => {
        try {
            setLoading(true);
            await driveClient.activateIntegration(integrationId);
            await loadIntegrations(); // Reload the list
        }
        catch (err) {
            console.error('Error activating Google Drive integration:', err);
            setError('Failed to activate Google Drive integration');
        }
        finally {
            setLoading(false);
        }
    };
    return (React.createElement(Segment, { style: { boxShadow: 'none', border: '1px solid #f0f0f0' } },
        error && React.createElement(InComponentErrorRenderer, { error: error }),
        integrations.length === 0 ? (React.createElement("div", null,
            React.createElement("p", { style: { color: '#666', marginBottom: '1em' } }, "Connect your Google Drive account to enable file storage and management for form submissions."),
            React.createElement(Button, { color: "blue", icon: true, labelPosition: "left", onClick: handleConnect, loading: loading, disabled: loading },
                React.createElement(Icon, { name: "google drive" }),
                "Connect Google Drive"))) : (React.createElement("div", null,
            React.createElement("p", { style: { color: '#666', marginBottom: '1em' } }, "Connected Google Drive accounts:"),
            React.createElement(List, { divided: true }, integrations.map((integration) => (React.createElement(List.Item, { key: integration.integrationId, style: { padding: '0.5em 0' } },
                React.createElement(List.Content, { floated: "right" },
                    React.createElement(Button, { size: "tiny", color: "red", icon: "trash", onClick: () => handleDisconnect(integration.integrationId), loading: loading, disabled: loading })),
                React.createElement(List.Content, null,
                    React.createElement(List.Header, null,
                        React.createElement(Icon, { name: "google drive", color: "blue" }),
                        integration.name),
                    React.createElement(List.Description, { style: { fontSize: '0.85em', color: '#666' } },
                        integration.userEmail,
                        " \u2022 Connected on ",
                        new Date(integration.createdAt).toLocaleDateString())))))),
            React.createElement(Button, { basic: true, icon: true, labelPosition: "left", onClick: handleConnect, loading: loading, disabled: loading, style: { marginTop: '1em' } },
                React.createElement(Icon, { name: "plus" }),
                "Connect Another Account"))),
        React.createElement("div", { style: { marginTop: '1.5em', padding: '1em', backgroundColor: '#f9f9f9', borderRadius: '5px' } },
            React.createElement("h4", { style: { margin: '0 0 0.5em 0', fontSize: '0.9em', color: '#666' } }, "What happens next?"),
            React.createElement("ul", { style: { margin: 0, paddingLeft: '1.2em', fontSize: '0.8em', color: '#666' } },
                React.createElement("li", null, "You'll authorize FluidWeb to access your Google Drive"),
                React.createElement("li", null, "We only access files you explicitly share with our app"),
                React.createElement("li", null, "You can revoke access anytime from your Google account"),
                React.createElement("li", null, "Your files remain private and secure")))));
};
